import {
  ActionIcon,
  Text,
  Flex,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
} from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { IconCircleFilled, IconSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi, useIsOnline } from "src/hooks";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";
import { parseErrorMessage } from "src/utils";
import { prefetchLogbookEntry } from "../../utils/navigateToLogbookEntry";
import { SelectItem as SelectItemBox } from "../EntrySelectItem/EntrySelectItem.styled";
import {
  logBarSelectComboboxProps,
  logBarSelectClassNames,
  logBarSelectStyles,
} from "../logBarSelectStyles";
import { statusList } from "../StatusBadge/StatusBadge";

type SearchOption = ComboboxItem & {
  status: number;
  mention?: string;
};

const SearchSelectItem = ({
  label,
  status,
  mention,
  showMention,
}: SearchOption & { showMention?: boolean }) => {
  return (
    <SelectItemBox>
      <Flex direction="column" style={{ flex: 1 }}>
        {label}
        {showMention && mention && (
          <Text size="xs" ml={5}>
            {mention}
          </Text>
        )}
      </Flex>
      <Text c={statusList[status]?.color}>
        <IconCircleFilled size={12} />
      </Text>
    </SelectItemBox>
  );
};

interface SearchEntry {
  lbeid?: string;
  meid?: string;
  partid?: string;
  enum: number;
  datecreated: string;
  entrydate: string;
  status: number;
  [key: string]: any;
}

const SearchRefInput = () => {
  const navigate = useNavigate();
  const { logbookId, idType } = useParams();
  const isOnline = useIsOnline();
  const [refSearch, setRefSearch] = useState("");
  const [searchResults, setSearchResults] = useState<SearchEntry[]>([]);
  const [isUserSearch, setIsUserSearch] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const yachtid = getYachtId();

  const { refetch } = useGetApi({
    request: "search-entry-ref",
    body: {},
    fetchOnInit: false,
  });
  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsUserSearch(false);
        return;
      }

      try {
        const data = (await refetch({
          request: "search-entry-ref",
          params: {
            yachtid,
            ref: searchTerm.trim(),
            ...(logbookId && { lbid: logbookId }),
          },
        })) as { list: SearchEntry[]; isUserSearch?: boolean };

        if (data?.list) {
          setSearchResults(data.list);
          setIsUserSearch(!!data.isUserSearch);
        }
      } catch (error: any) {
        setSearchResults([]);
        setIsUserSearch(false);
      }
    },
    [yachtid, logbookId, refetch],
  );

  const handleSearchRef = useCallback(
    (searchTerm: string) => {
      setRefSearch(searchTerm);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsUserSearch(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    },
    [performSearch],
  );

  const selectData = useMemo(
    () =>
      searchResults
        .filter(s => s.status !== 99)
        .map((entry: SearchEntry) => {
          const entryId =
            entry[
              idType === "meid"
                ? "meid"
                : idType === "partid"
                  ? "partid"
                  : "lbeid"
            ];
          const date = entry.datecreated || "";
          const formattedDate = date ? dayjs(date).format("D MMM YYYY") : "";

          return {
            value: entryId ?? "",
            label: `#${entry.enum ?? ""} - ${formattedDate}`,
            status: entry.status,
            mention: entry.mention ?? "",
          };
        }),
    [searchResults, idType],
  );

  const handleSelectChange = useCallback(
    async (value: string | null) => {
      if (!value || !logbookId || !idType) return;

      const navigateToEntry = () => {
        setRefSearch("");
        setSearchResults([]);
        setIsUserSearch(false);
        navigate(`/logbook/${logbookId}/${idType}/${value}`, {
          replace: true,
        });
      };

      if (!isOnline && window?.api?.get) {
        const ok = await prefetchLogbookEntry(logbookId, idType, value);
        if (!ok) {
          Notification(
            "Error",
            parseErrorMessage(
              "Unable to load data as you appear to be offline. get-logbook",
            ),
            "red",
          );
          return;
        }
      }

      navigateToEntry();
    },
    [idType, isOnline, logbookId, navigate],
  );

  return (
    <Select
      w={220}
      placeholder="Search Ref # / Name"
      leftSection={<IconSearch size={16} />}
      searchValue={refSearch}
      onSearchChange={handleSearchRef}
      onChange={handleSelectChange}
      filter={({ options }) => options}
      data={selectData}
      styles={logBarSelectStyles}
      classNames={logBarSelectClassNames}
      comboboxProps={logBarSelectComboboxProps}
      renderOption={({
        option,
      }: ComboboxLikeRenderOptionInput<SearchOption>) => (
        <SearchSelectItem showMention={isUserSearch} {...option} />
      )}
      value={null}
      searchable
      clearable
    />
  );
};

export default SearchRefInput;
